import type { RealTimeEntryRecord } from '../../data/use_time_entries';
import type { TimeEntriesState } from '../../data/use_time_entries';
import type { Employee } from '../../data/types';

/**
 * What the Timesheets screen should render for a given `TimeEntriesState` +
 * row count. Extracted as a pure, independently-tested function — mirrors
 * `expense_utils.ts`'s `resolve_expenses_view`.
 */
export type TimesheetsView =
  | 'loading'
  | 'no_entity'
  | 'entitlement_required'
  | 'error'
  | 'empty'
  | 'rows';

export function resolve_timesheets_view(input: {
  state: TimeEntriesState;
  row_count: number;
}): TimesheetsView {
  if (input.state.status === 'loading') return 'loading';
  if (input.state.status === 'no_entity') return 'no_entity';
  if (input.state.status === 'entitlement_required') {
    return 'entitlement_required';
  }
  if (input.state.status === 'error') return 'error';
  return input.row_count === 0 ? 'empty' : 'rows';
}

/**
 * Sort a copy of `entries` by `work_date` descending (most recent first),
 * breaking ties by `created_at` descending so a same-day pair is still
 * deterministic. Returns a new array — never mutates the input.
 */
export function sort_by_work_date_desc(
  entries: readonly RealTimeEntryRecord[],
): RealTimeEntryRecord[] {
  return [...entries].sort((a, b) => {
    if (a.work_date !== b.work_date) {
      return a.work_date < b.work_date ? 1 : -1;
    }
    if (a.created_at !== b.created_at) {
      return a.created_at < b.created_at ? 1 : -1;
    }
    return 0;
  });
}

/**
 * Look up an employee's display name by id. Returns the fallback key value
 * when the employee is missing (e.g. deactivated after the time entry was
 * logged) — the caller passes a localised `t('timesheets.unknown_employee')`.
 */
export function resolve_employee_name(
  employees: readonly Employee[],
  employee_id: string,
  fallback: string,
): string {
  const match = employees.find((emp) => emp.id === employee_id);
  return match?.name ?? fallback;
}

/**
 * The i18n key for the human-readable explanation of a `create` failure.
 * Mirrors `expense_action_error_key` in `expense_utils.ts`.
 */
export function create_time_entry_error_key(
  failure: 'entitlement_required' | 'forbidden' | 'not_found' | 'error',
): string {
  switch (failure) {
    case 'entitlement_required':
      return 'timesheets.form.error_entitlement_required';
    case 'forbidden':
      return 'timesheets.form.error_forbidden';
    case 'not_found':
      return 'timesheets.form.error_not_found';
    case 'error':
      return 'timesheets.form.error_generic';
  }
}
